import {
  CAPITAL_GLOBAL_CORRESPONDENCE_VALIDATE_FIELD_NAMES,
  capitalGlobalSubStipulatorAddressFieldNames,
} from '@/features/proposals/components/capital-global/constants/capital-global-proposal-address-field-names.constant';
import { isCaepfDocument } from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import type { CapitalGlobalProposalStepDadosDoSeguroValues } from '@/features/proposals/schemas/capital-global/proposal-step-dados-do-seguro.schema';

/** Campos com validadores no Step 1 — cadastro e contatos. */
const BASE_FIELD_NAMES = [
  'effectiveStartDate',
  'registrationCep',
  'registrationStreet',
  'registrationNumber',
  'registrationNeighborhood',
  'registrationCity',
  'registrationState',
  'stipulatorPhone',
  'stipulatorEmail',
  'privacyAccepted',
] as const satisfies readonly (keyof CapitalGlobalProposalStepDadosDoSeguroValues)[];

/**
 * CAEPF (pessoa física): o i4Pro exige sexo e data de nascimento do titular — tanto do
 * estipulante quanto de cada subestipulante. A Cotação manda os dois vazios de
 * propósito, então a Proposta é o único ponto de captura: fora desta lista o "Próximo"
 * avança em branco e a falta só aparece na emissão.
 */
const CAEPF_FIELD_NAMES = [
  'gender',
  'dateOfBirth',
] as const satisfies readonly (keyof CapitalGlobalProposalStepDadosDoSeguroValues)[];

export type CapitalGlobalStep1DocumentMask = 'cnpj' | 'cpf' | 'caepf';

/** Campos do estipulante que o "Próximo" precisa validar. */
export function capitalGlobalStep1ValidateFieldNames({
  sameCorrespondenceAsRegistration,
  documentMask,
}: {
  sameCorrespondenceAsRegistration: boolean;
  documentMask: CapitalGlobalStep1DocumentMask;
}): string[] {
  return [
    ...BASE_FIELD_NAMES,
    ...(sameCorrespondenceAsRegistration
      ? []
      : CAPITAL_GLOBAL_CORRESPONDENCE_VALIDATE_FIELD_NAMES),
    ...(documentMask === 'caepf' ? CAEPF_FIELD_NAMES : []),
  ];
}

/** Só o que o teste do "Próximo" precisa saber de cada linha de subestipulante. */
export type CapitalGlobalStep1SubStipulatorLike = {
  isCompany?: boolean;
  documentNumber?: string;
};

/**
 * Campos de cada subestipulante, agrupados por índice — o `handleSubmit` precisa saber
 * QUAL bloco reprovou para abrir o accordion (erro dentro de accordion fechado é erro
 * invisível).
 *
 * Endereço e contato são sempre obrigatórios: sem endereço o i4Pro recebe o `idEndereco`
 * do principal para todos (fallback do erro 17) e a apólice sai com endereço errado.
 */
export function capitalGlobalStep1SubStipulatorValidateFieldNames(
  entries: readonly CapitalGlobalStep1SubStipulatorLike[]
): string[][] {
  return entries.map((entry, index) => {
    const f = capitalGlobalSubStipulatorAddressFieldNames(index);
    // Mesma condição do render da linha: CAEPF é pessoa física com 14 dígitos
    // (CNPJ também tem 14, por isso o `isCompany` entra no teste).
    const isCaepfSub =
      !entry.isCompany && isCaepfDocument(entry.documentNumber || '');
    return [
      f.cep,
      f.street,
      f.number,
      f.neighborhood,
      f.city,
      f.state,
      `subStipulatorAddresses[${index}].phone`,
      `subStipulatorAddresses[${index}].email`,
      ...(isCaepfSub
        ? CAEPF_FIELD_NAMES.map(
            name => `subStipulatorAddresses[${index}].${name}`
          )
        : []),
    ];
  });
}
